// The replay workbench: fire real shots from the database through the vision
// pipeline with the contract edited on the fly, and see what CharlesBot would
// have said. Nothing is stored - this is the in-browser counterpart of
// scripts/replay_shot_reviews.py, for trialling a prompt before shipping it.
//
// "The contract" is three things, and all three have to be editable together:
// the wording, the shape of the conversation (whether a zoom is screened for,
// sent up front, or not offered at all) and the JSON schema the reply must
// match. A prompt edited alone is a prompt overruled - the model can only
// answer the question its schema asks, and the pipeline's follow-up turns
// refer it back to "the JSON described above" whatever it was told.
//
// The Escalate button is the other rung on the same terms: the stronger model
// on a shot, verdict and transcript, stored nowhere. It is not a variant of
// the contract above - its prompt is built from the candidate ranking rather
// than typed, so none of the boxes reach it - and it is kept in separate
// state from the review replay, because comparing the two rungs on one card
// is the reason to have both.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { sendAPIRequest } from "./utils";
import { AdminPage } from "./AdminCommon";
import { getShotFromCache } from "./ShotCache";
import { usePatchSearchParams } from "./urlState";
import {
  ChannelTags,
  ShotEscalation,
  outcomeTag,
  verdictText,
  zoomTag,
} from "./ShotQueue";
import { Row, Col } from "react-bootstrap";

import styles from "./ShotReplay.module.css";
// The outcome tags are the ShotQueue's own, so a review reads the same here as
// in the queue.
import tagStyles from "./ShotQueue.module.css";

// A live game leaves hundreds of shots in the database, and this page used to
// mount a ShotCard - full vision images fetched and all - for every one of
// them at once. That is what choked: a page of cards is a page of concurrent
// fetches, not a page of history. The page number lives in the query string
// (../urlState.js), like every other "what are you looking at it through" on
// an admin page, so a reload comes back to where the admin was.
const PAGE_SIZE = 20;

// The conversation shapes the backend offers (shot_vision.ZOOM_MODES), and
// what each does to the exchange.
const ZOOM_MODES = [
  ["screened", "Screened zoom (live pipeline)"],
  ["upfront", "Both views up front"],
  ["single", "One turn, no zoom"],
];

// The coarse verdict a review implies, for comparison with the admin's own.
const OUTCOME_TO_VERDICT = {
  hit_player: "hit",
  hit_bystander: "bystander",
  miss: "miss",
};

// One shot's reading, rendered from the review dict the replay endpoint
// returns (same shape ShotAiTags displays in the queue).
function ReplayResult({ review }) {
  // Under a contract of its own the reply has no outcome to render, and the
  // pipeline's default one would be a verdict the model never gave.
  if (review.parse_error) {
    return (
      <>
        <p className={styles.rawReplyNote}>
          The reply did not match the standard reading: {review.parse_error}
        </p>
        <pre className={styles.rawReply}>
          {JSON.stringify(review.raw_reply, null, 2)}
        </pre>
      </>
    );
  }

  return (
    <>
      <div className={tagStyles.tagRow}>
        {outcomeTag(review)}
        <span className={tagStyles.tag}>
          confidence {Math.round(100 * (review.confidence || 0))}%
        </span>
        {zoomTag(review)}
        <ChannelTags channels={review.channels} />
      </div>
      <p className={tagStyles.aiReason}>
        {review.outcome_reason}
        {review.reasoning ? ` - ${review.reasoning}` : null}
      </p>
    </>
  );
}

// Every turn exchanged with the model on one replay, and what it said back --
// the debugging counterpart of ReplayResult's parsed summary. A flat,
// chronological conversation (the model never revises an earlier turn, so
// nothing here is ever repeated), collapsed by default. A raw-JSON toggle
// dumps the same list pretty-printed, for pasting elsewhere.
function TranscriptView({ transcript }) {
  const [raw, setRaw] = useState(false);

  if (!transcript || transcript.length === 0) return null;

  return (
    <details className={styles.transcript}>
      <summary>
        Full model transcript ({transcript.length} turn
        {transcript.length === 1 ? "" : "s"})
      </summary>
      <label className={styles.rawToggle}>
        <input
          type="checkbox"
          checked={raw}
          onChange={(event) => setRaw(event.target.checked)}
        />
        Prettified JSON
      </label>
      {raw ? (
        <pre className={styles.transcriptJson}>
          {JSON.stringify(transcript, null, 2)}
        </pre>
      ) : (
        transcript.map((turn, turnIndex) => (
          <div key={turnIndex} className={styles.turn}>
            <span className={styles.turnRole}>
              {turn.role}
              {turn.has_image ? " (+ image)" : ""}
            </span>
            {turn.reasoning ? (
              <details className={styles.turnReasoning}>
                <summary>Model reasoning</summary>
                <pre className={styles.turnReasoningText}>{turn.reasoning}</pre>
              </details>
            ) : null}
            <pre className={styles.turnText}>
              {turn.role === "assistant"
                ? JSON.stringify(turn.reply, null, 2)
                : turn.text}
            </pre>
          </div>
        ))
      )}
    </details>
  );
}

// Vision-formatted images, in the order the model actually saw them: the full
// frame, then one card per zoom actually spent (zoomCount, 0-2) -- not just
// the two the pipeline is *capable* of, which said nothing about what a given
// replay did. Before a replay has run (zoomCount undefined) only the full
// frame is shown, since nothing is known yet about whether a zoom followed.
// Fetched from admin_get_shot_vision_images, which formats identically to the
// pipeline (prepare_for_vision + zoom_image).
export function ShotVisionImages({ shot_id, zoomCount }) {
  const [images, setImages] = useState(null);

  useEffect(() => {
    setImages(null);
    if (!shot_id) return;
    let cancelled = false;
    sendAPIRequest("admin_get_shot_vision_images", { shot_id }).then(
      async (response) => {
        if (cancelled) return;
        if (!response.ok) return;
        const body = await response.json();
        if (!cancelled) setImages(body);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [shot_id]);

  if (!images)
    return <p className={styles.visionLoading}>Loading vision images...</p>;

  const zoomImages = [images.zoomed, images.zoomed2].slice(0, zoomCount || 0);

  return (
    <div className={styles.visionImages}>
      <div className={styles.visionImageWrapper}>
        <img
          className={styles.visionImg}
          alt="Full frame as vision sees it"
          src={images.full}
        />
        <span className={styles.visionLabel}>
          Full frame (as vision sees it)
        </span>
      </div>
      {zoomImages.map((src, zoomIndex) => (
        <div key={zoomIndex} className={styles.visionImageWrapper}>
          <img
            className={styles.visionImg}
            alt={`Zoom ${zoomIndex + 1} centre as vision sees it`}
            src={src}
          />
          <span className={styles.visionLabel}>
            Zoom {zoomIndex + 1} centre (as vision sees it)
          </span>
        </div>
      ))}
    </div>
  );
}

// One selectable shot: thumbnail, shooter, the admin's verdict if there is
// one, and whatever the last replay run said about it - the cheap pass's
// reading and the escalation are separate runs, so a card can show one, the
// other, or both side by side.
function ShotCard({ shot_id, selected, onToggle, result, escalation }) {
  const [shot, setShot] = useState(null);

  useEffect(() => {
    setShot(null);
    let cancelled = false;
    getShotFromCache(shot_id).then((s) => {
      if (!cancelled) setShot(s);
    });
    return () => {
      cancelled = true;
    };
  }, [shot_id]);

  if (!shot) return <div className={styles.card}>Loading...</div>;

  const replayVerdict =
    result && result.review ? OUTCOME_TO_VERDICT[result.review.outcome] : null;
  const disagrees =
    shot.checked && replayVerdict !== null && shot.result !== replayVerdict;

  return (
    <div
      className={`${styles.card} ${selected ? styles.cardSelected : ""}`}
      onClick={onToggle}
    >
      <label className={styles.cardPicker} onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={selected} onChange={onToggle} />
      </label>
      <div className={styles.cardBody}>
        <em>
          {shot.user.name} - {new Date(shot.time_created).toLocaleString()}
        </em>
        <ShotVisionImages
          shot_id={shot_id}
          zoomCount={result && result.review ? result.review.zoom_count : 0}
        />
        {shot.checked ? (
          <p className={tagStyles.verdict}>Adjudicated: {verdictText(shot)}</p>
        ) : (
          <p>Not yet adjudicated</p>
        )}
        {result && result.status === "running" ? <p>Replaying...</p> : null}
        {result && result.status === "error" ? (
          <p className={styles.replayError}>Replay failed: {result.error}</p>
        ) : null}
        {result && result.review ? (
          <div onClick={(e) => e.stopPropagation()}>
            {disagrees ? (
              <p className={styles.disagreement}>
                Disagrees with the admin's verdict
              </p>
            ) : null}
            <ReplayResult review={result.review} />
            <TranscriptView transcript={result.review.transcript} />
          </div>
        ) : null}
        {escalation && escalation.status === "running" ? (
          <p>Escalating...</p>
        ) : null}
        {escalation && escalation.status === "error" ? (
          <p className={styles.replayError}>
            Escalation replay failed: {escalation.error}
          </p>
        ) : null}
        {escalation && escalation.payload ? (
          <div onClick={(e) => e.stopPropagation()}>
            {/* The queue's own renderer, told the run is done: a trial
                escalation must read exactly like a stored one. */}
            <ShotEscalation state="done" escalation={escalation.payload} />
            <TranscriptView transcript={escalation.payload.transcript} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// The page's only navigation: which PAGE_SIZE-wide slice of the (newest
// first) shot list is actually mounted. Renders nothing for a list that
// already fits on one page, so the common case - a fresh dev database, or a
// game with a handful of shots - looks exactly as it did before pagination.
function Pager({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  return (
    <div className={styles.pager}>
      <button onClick={() => onChange(1)} disabled={page === 1}>
        « First
      </button>
      <button onClick={() => onChange(page - 1)} disabled={page === 1}>
        ‹ Previous
      </button>
      <span>
        Page {page} of {totalPages}
      </span>
      <button onClick={() => onChange(page + 1)} disabled={page === totalPages}>
        Next ›
      </button>
      <button
        onClick={() => onChange(totalPages)}
        disabled={page === totalPages}
      >
        Last »
      </button>
    </div>
  );
}

function ShotReplayPanel() {
  const [shotIds, setShotIds] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [prompt, setPrompt] = useState(null);
  const [schemaText, setSchemaText] = useState(null);
  const [schemaError, setSchemaError] = useState(null);
  const [zoomMode, setZoomMode] = useState(ZOOM_MODES[0][0]);
  // "" means no override -- the live pipeline's OPENROUTER_REASONING_EFFORT
  // setting (or none at all) applies, exactly as it does for a real review.
  const [reasoningEffort, setReasoningEffort] = useState("");
  const [results, setResults] = useState({});
  // Escalation runs are kept apart from review runs rather than replacing
  // them: the point of the second rung is reading it against the first.
  const [escalations, setEscalations] = useState({});
  const [running, setRunning] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [searchParams] = useSearchParams();
  const patchSearchParams = usePatchSearchParams();
  const totalPages = Math.max(1, Math.ceil(shotIds.length / PAGE_SIZE));
  const requestedPage = parseInt(searchParams.get("page"), 10);
  const page =
    Number.isFinite(requestedPage) && requestedPage >= 1
      ? Math.min(requestedPage, totalPages)
      : 1;
  const pageShotIds = shotIds.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const goToPage = useCallback(
    (next) => patchSearchParams({ page: next <= 1 ? null : next }),
    [patchSearchParams],
  );
  // What the backend last handed back, so an *untouched* box can follow a
  // change of conversation shape while an edited one is never clobbered.
  const seeded = useRef({ prompt: null, schema: null });

  const seedContract = useCallback((mode, force) => {
    sendAPIRequest(
      "admin_get_default_vision_prompt",
      { zoom_mode: mode },
      "GET",
      (body) => {
        const nextSchema = JSON.stringify(body.schema, null, 2);
        // Read against what was seeded *before* this fetch: the state updaters
        // below run after this callback returns, so comparing them against the
        // ref once it already holds the new values would find no match and
        // leave every box stale.
        const previous = seeded.current;
        seeded.current = { prompt: body.prompt, schema: nextSchema };
        setPrompt((current) =>
          force || current === null || current === previous.prompt
            ? body.prompt
            : current,
        );
        setSchemaText((current) =>
          force || current === null || current === previous.schema
            ? nextSchema
            : current,
        );
        setSchemaError(null);
      },
    );
  }, []);

  // Every shot in the database, newest first: this page replays history, so
  // the adjudicated shots the queue hides are the interesting ones.
  useEffect(() => {
    sendAPIRequest("admin_get_shots_info", { include_checked: true }).then(
      async (response) => {
        if (!response.ok) return;
        setShotIds((await response.json()).reverse());
      },
    );
  }, []);

  // The prompt describes the zoom it is about to be offered, so the default
  // text is only correct for one shape of conversation: reseed on a change.
  useEffect(() => {
    seedContract(zoomMode, false);
  }, [zoomMode, seedContract]);

  const toggle = useCallback((shot_id) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(shot_id)) {
        next.delete(shot_id);
      } else {
        next.add(shot_id);
      }
      return next;
    });
  }, []);

  const setResult = useCallback((shot_id, result) => {
    setResults((previous) => ({ ...previous, [shot_id]: result }));
  }, []);

  const setEscalation = useCallback((shot_id, escalation) => {
    setEscalations((previous) => ({ ...previous, [shot_id]: escalation }));
  }, []);

  // The second rung, run the same way: no store, no verdict, no auto-actions.
  // Nothing above is sent - the escalation prompt is assembled from the
  // candidate ranking, so only the reasoning-effort knob reaches it.
  const escalateSelected = useCallback(() => {
    setEscalating(true);
    Promise.all(
      [...selected].map((shot_id) => {
        setEscalation(shot_id, { status: "running" });
        return sendAPIRequest(
          "admin_replay_shot_escalation",
          {},
          "POST",
          null,
          { shot_id, reasoning_effort: reasoningEffort || null },
        ).then(async (response) => {
          if (response.ok) {
            setEscalation(shot_id, {
              status: "done",
              payload: await response.json(),
            });
          } else {
            setEscalation(shot_id, {
              status: "error",
              error: `${response.status}: ${await response.text()}`,
            });
          }
        });
      }),
    ).then(() => setEscalating(false));
  }, [selected, reasoningEffort, setEscalation]);

  const replaySelected = useCallback(() => {
    let responseSchema;
    try {
      responseSchema = JSON.parse(schemaText);
    } catch (e) {
      setSchemaError(`The response schema is not valid JSON: ${e.message}`);
      return;
    }
    setSchemaError(null);
    setRunning(true);
    // Fire them all at once: the backend's semaphore bounds how many vision
    // calls are actually in flight.
    Promise.all(
      [...selected].map((shot_id) => {
        setResult(shot_id, { status: "running" });
        return sendAPIRequest("admin_replay_shot_review", {}, "POST", null, {
          shot_id,
          prompt,
          zoom_mode: zoomMode,
          response_schema: responseSchema,
          reasoning_effort: reasoningEffort || null,
        }).then(async (response) => {
          if (response.ok) {
            setResult(shot_id, {
              status: "done",
              review: await response.json(),
            });
          } else {
            setResult(shot_id, {
              status: "error",
              error: `${response.status}: ${await response.text()}`,
            });
          }
        });
      }),
    ).then(() => setRunning(false));
  }, [selected, prompt, schemaText, zoomMode, reasoningEffort, setResult]);

  return (
    <>
      <h1>Shot replay workbench</h1>
      <Row>
        <Col>
          {/* "CharlesBot" is the display name for what the API calls ai_review (#1). */}
          <p>
            Replays real shots through the CharlesBot reviewer with the contract
            below. Nothing is stored and the game is not affected. Pick shots,
            edit the prompt, the conversation shape and the schema its reply
            must match, run. A reply that is not a standard reading is shown as
            it landed.
          </p>
          <p>
            <strong>Escalate</strong> runs the second rung instead - the
            stronger model, shown the candidate ranking and their reference
            photos - and shows its verdict and transcript beside the reading. It
            uses each shot's <em>stored</em> review to build that ranking, like
            the queue's "Run escalated review" button does, so a shot nobody has
            reviewed cannot be escalated. Its prompt is assembled from the
            candidates rather than typed, so nothing in the boxes above reaches
            it; the reasoning-effort control does.
          </p>
          <textarea
            aria-label="Vision prompt"
            className={styles.promptBox}
            value={prompt === null ? "Loading the live prompt..." : prompt}
            onChange={(event) => setPrompt(event.target.value)}
          />
          <details className={styles.schemaPanel}>
            <summary>
              Response schema - the JSON shape the reply is forced into
            </summary>
            <textarea
              aria-label="Response schema"
              className={styles.schemaBox}
              value={
                schemaText === null ? "Loading the live schema..." : schemaText
              }
              onChange={(event) => setSchemaText(event.target.value)}
            />
          </details>
          {schemaError ? (
            <p className={styles.replayError}>{schemaError}</p>
          ) : null}
          <div className={styles.controls}>
            <button
              onClick={replaySelected}
              disabled={
                running ||
                selected.size === 0 ||
                prompt === null ||
                schemaText === null
              }
            >
              {running
                ? "Replaying..."
                : `Replay ${selected.size} selected shot${
                    selected.size === 1 ? "" : "s"
                  }`}
            </button>
            <button
              onClick={escalateSelected}
              disabled={escalating || selected.size === 0}
            >
              {escalating
                ? "Escalating..."
                : `Escalate ${selected.size} selected shot${
                    selected.size === 1 ? "" : "s"
                  }`}
            </button>
            <button onClick={() => seedContract(zoomMode, true)}>
              Reset to live contract
            </button>
            <label>
              Conversation shape{" "}
              <select
                aria-label="Conversation shape"
                value={zoomMode}
                onChange={(event) => setZoomMode(event.target.value)}
              >
                {ZOOM_MODES.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Reasoning effort{" "}
              <select
                aria-label="Reasoning effort"
                value={reasoningEffort}
                onChange={(event) => setReasoningEffort(event.target.value)}
              >
                <option value="">Pipeline default</option>
                <option value="none">none</option>
                <option value="minimal">minimal</option>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="xhigh">xhigh</option>
                <option value="max">max</option>
              </select>
            </label>
            <button
              onClick={() => setSelected(new Set(shotIds))}
              disabled={shotIds.length === 0}
            >
              Select all
            </button>
            <button onClick={() => setSelected(new Set())}>Select none</button>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Pager page={page} totalPages={totalPages} onChange={goToPage} />
          <div className={styles.grid}>
            {pageShotIds.map((shot_id) => (
              <ShotCard
                key={shot_id}
                shot_id={shot_id}
                selected={selected.has(shot_id)}
                onToggle={() => toggle(shot_id)}
                result={results[shot_id]}
                escalation={escalations[shot_id]}
              />
            ))}
          </div>
          <Pager page={page} totalPages={totalPages} onChange={goToPage} />
        </Col>
      </Row>
    </>
  );
}

export default function ShotReplay() {
  return (
    <AdminPage>
      <ShotReplayPanel />
    </AdminPage>
  );
}
